import { useCallback, useEffect, useRef, useState } from 'react';
import { MakeTag, resetJobMakes } from '../lib/jobMakes.tsx';
import { api, fmt, fmtDate, poNo, type CardPart, type Stage, type User } from '../api.ts';
import { BOX_TYPES, JOB_WORK_LABEL, boxType, typeOfMake } from '../lib/boxTypes.ts';
import JobCardPrint, { PRIORITY_LABEL, fmtStamp, type Card, type PrintStamp }
  from '../components/JobCardPrint.tsx';
import OptionInput from '../components/OptionInput.tsx';
import SearchSelect from '../components/SearchSelect.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';
import UndoDialog from '../components/UndoDialog.tsx';
import SplitDialog from '../components/SplitDialog.tsx';
import DeliveryDateDialog from '../components/DeliveryDateDialog.tsx';
import { LayersEditor, LayersView, PlyPicker, type FaceBoard } from '../components/Layers.tsx';
import {
  ONE_PLY, draftsOf, fluteOfBoxType, layersBody, layersComplete, layersFor, plyName, type LayerDraft,
} from '../lib/layers.ts';
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/**
 * Numeric columns arrive as strings with trailing zeros ("1025.000"), which
 * looks wrong in an input an operator is editing. Normalise via Number so the
 * field shows 1025.
 */
const numStr = (v: unknown) =>
  v === null || v === undefined || v === '' ? '' : String(Number(v));

/** One board on the card, from Board stock (053), with where it stands. */
interface JobBoard {
  item_id: number; item_name: string; sheets: string; paired: string;
  available: string; short: string; free: string; wanted: string;
  status: 'PAIRED' | 'AVAILABLE' | 'SHARED' | 'SHORT' | 'ISSUED' | 'CANCELLED';
  /** 056: typed sheets the run has moved under, and issued against the card. */
  run_needs: string | null; out_of_date: boolean; issued: string; to_issue: string;
  set_by_hand: boolean;
  /** 060: board on order from a supplier for this job. */
  on_order: string; on_order_due: string | null; on_order_from: string | null;
}
interface BoardContext {
  status: string; jobNumber: string; productionStarted: boolean;
  boardRequired: boolean; ups: number | null;
  productionQuantity: number; suggestedSheets: number | null;
}
/** Released without its board, or lent board to a job that was (066). */
interface BoardExceptions {
  released_short?: boolean; board_pending?: string; borrowed_owed?: string; borrowed_from?: string | null;
  lent_owed?: string; lent_to?: string | null;
  history: { kind: 'LOAN' | 'SUBSTITUTE' | 'PENDING'; sheets_short: string; reason: string; created_at: string;
             by_name: string; item_name: string; substitute_name: string | null; lender_job_number: string | null }[];
}
interface BoardOption {
  item_id: number; name: string; on_hand: string; free: string; length_cm: string; width_cm: string;
}
type BoardDraft = { itemId: string; sheets: string };

const fmtN = (v: string | number) => Number(v).toLocaleString('en-IN');

/** "91.5 X 61 CM", "61x91.5" -> [91.5, 61], larger side first; null unless two numbers. */
function parseSize(v: string | null | undefined): [number, number] | null {
  const m = (v ?? '').match(/(\d+(?:\.\d+)?)\s*[x×*]\s*(\d+(?:\.\d+)?)/i);
  if (!m) return null;
  const a = Number(m[1]); const b = Number(m[2]);
  return a >= b ? [a, b] : [b, a];
}

/**
 * The order plus the waste boards' worth of boxes -- the same sum the server
 * does -- so the sheets worked out from the run are the order's plus the waste.
 */
const runFor = (ordered: number, wasteSheets: number, ups: number | null) =>
  ordered + (ups && ups > 0 ? wasteSheets * ups : 0);

const upsOf = (v: string) => (/^\d+$/.test(v.trim()) && Number(v) > 0 ? Number(v) : null);

interface UpsRun { jobId: number; jobNumber: string; run: number; sheets: number }

type Section = 'qty' | 'route' | 'printing' | 'corrugation' | 'notes';

const SECTION_NAME: Record<Section, string> = {
  qty: 'Quantity and board', route: 'Route', printing: 'Printing card',
  corrugation: 'Corrugation card', notes: 'Instruction',
};

type CardFields = {
  plateType: string; plateSetNumber: string; plateSave: boolean; colour: string; printingSide: string;
  runningSize: string; finishingWork: string; instructions: string; designReference: string; ply: string;
  kraftPrinting: boolean;
};
/** Everything the panels' forms hold. */
type Drafts = {
  f: CardFields; routeIds: number[]; qty: { ordered: string; production: string };
  upsDraft: string; boardDraft: BoardDraft[]; liners: LayerDraft[];
};
/** The card fields each panel edits. */
const SECTION_FIELDS: Record<Section, (keyof CardFields)[]> = {
  qty: [], route: [],
  printing: ['plateType', 'plateSetNumber', 'plateSave', 'colour', 'printingSide', 'runningSize',
             'finishingWork', 'designReference'],
  corrugation: ['ply', 'kraftPrinting'], notes: ['instructions'],
};

/** The forms as the card stands saved: where a panel starts, and what Cancel puts back. */
function cardDrafts(c: Card, boards: JobBoard[]): Drafts {
  const route = (c.route ?? []).map((r) => r.stageId);
  return {
    f: {
      plateType: str(c.plate_type), plateSetNumber: str(c.plate_set_number), plateSave: c.plate_save,
      colour: str(c.colour), printingSide: str(c.printing_side), runningSize: str(c.running_size),
      finishingWork: str(c.finishing_work), instructions: str(c.instructions),
      designReference: str(c.design_reference), ply: numStr(c.ply), kraftPrinting: Boolean(c.kraft_printing),
    },
    routeIds: route.length === 0 && c.job_status === 'DRAFT' ? (c.suggested_route ?? []) : route,
    qty: { ordered: numStr(c.ordered_quantity), production: numStr(c.production_quantity) },
    upsDraft: numStr(c.ups),
    boardDraft: boards.length
      ? boards.map((r) => ({ itemId: String(r.item_id), sheets: numStr(r.sheets) }))
      : [{ itemId: '', sheets: '' }],
    liners: draftsOf(c.liners ?? []),
  };
}

/** The part of the forms one panel owns -- compared with the saved card to tell unsaved typing. */
function sectionDraft(d: Drafts, s: Section): unknown {
  const fields = SECTION_FIELDS[s].map((k) => d.f[k]);
  if (s === 'qty') return [d.qty, d.upsDraft, d.boardDraft];
  if (s === 'route') return d.routeIds;
  if (s === 'corrugation') return [fields, d.liners];
  return fields;
}

const SHEET_NAME: Record<CardPart, string> = { PRINTING: 'Printing card', CORRUGATION: 'Corrugation card' };

/** One print in the card's history (086). */
interface PrintRow { id: number; part: CardPart; revision: number | null; printed_at: string; printed_by: string | null }

const STATUS: Record<string, [string, string]> = {
  DRAFT: ['Draft', 'blocked'], CREATED: ['Released', 'high'], IN_PRODUCTION: ['In production', 'high'],
  ON_HOLD: ['On hold', 'waiting'], COMPLETED: ['Completed', 'ready'], DISPATCHED: ['Dispatched', 'ready'],
  CANCELLED: ['Cancelled', 'late'],
};

/** How a board on the card stands against stock, as one tag. */
function BoardStatus({ b }: { b: JobBoard }) {
  const n = (v: string) => Number(v).toLocaleString('en-IN');
  switch (b.status) {
    case 'PAIRED': return <span className="tag ready">Paired · stocked in for this job</span>;
    case 'AVAILABLE': return <span className="tag ready">Available</span>;
    case 'SHARED':
      return (
        <span className="tag waiting"
              title={`${n(b.free)} free · waiting jobs want ${n(b.wanted)} — whichever goes to production first takes it`}>
          Available · shared
        </span>
      );
    case 'SHORT': return <span className="tag waiting">Short {n(b.short)}</span>;
    case 'CANCELLED': return <span className="dim">Job cancelled</span>;
    default: return <span className="tag blocked">Issued {n(b.issued)}</span>;
  }
}

export default function JobCard({
  jobId, part, startEditing = false, user, onBack,
}: {
  jobId: number; part?: CardPart | undefined;
  /** Opened from a list to fix the card. It opens like any other -- nothing in
   *  edit -- and Back tells that list what was saved. */
  startEditing?: boolean;
  user: User; onBack: (msg?: string) => void;
}) {
  const [card, setCard] = useState<Card | null>(null);
  /** The panels open for editing. Each saves or cancels on its own; the rest stay as they are. */
  const [open, setOpenState] = useState<ReadonlySet<Section>>(new Set());
  const openRef = useRef<ReadonlySet<Section>>(open);
  const setOpen = (next: ReadonlySet<Section>) => { openRef.current = next; setOpenState(next); };
  const editing = open.size > 0;
  /** A save happened since the card was opened from a list: Back tells the list. */
  const savedThisVisit = useRef(false);
  /** What the next print puts on paper: one card, or both. */
  const [printParts, setPrintParts] = useState<CardPart[]>([]);
  /** What the last print recorded, for the paper to carry (086). */
  const [stamp, setStamp] = useState<PrintStamp | null>(null);
  const [history, setHistory] = useState<PrintRow[] | null>(null);
  const [printMenu, setPrintMenu] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const isManager = user.role === 'ADMIN' || user.role === 'MANAGER';
  // Locked once the job goes into production: the floor is holding a printed
  // copy by then, and editing would make the paper disagree with the system.
  const locked = card !== null && !card.card_editable;
  const canEdit = isManager && !locked;

  // Printing fields
  const [f, setF] = useState<CardFields>({
    plateType: '', plateSetNumber: '', plateSave: false, colour: '', printingSide: '',
    runningSize: '', finishingWork: '', instructions: '', designReference: '', ply: '', kraftPrinting: false,
  });
  const [boards, setBoards] = useState<JobBoard[]>([]);
  const [boardCtx, setBoardCtx] = useState<BoardContext | null>(null);
  const [boardExceptions, setBoardExceptions] = useState<BoardExceptions | null>(null);
  const [boardDraft, setBoardDraft] = useState<BoardDraft[]>([]);
  const [boardOptions, setBoardOptions] = useState<BoardOption[]>([]);
  const [liners, setLiners] = useState<LayerDraft[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [routeIds, setRouteIds] = useState<number[]>([]);
  const [kind, setKind] = useState<string | null>(null);
  const [qty, setQty] = useState({ ordered: '', production: '' });
  const [adding, setAdding] = useState<{ stageId: string; reason: string } | null>(null);
  const [undoUrl, setUndoUrl] = useState<string | null>(null);
  const [splitting, setSplitting] = useState(false);
  const [upsDraft, setUpsDraft] = useState('');
  const [linePlan, setLinePlan] = useState<UpsRun[] | null>(null);
  const runTyped = useRef(false);
  const [noticeUndo, setNoticeUndo] = useState<{ text: string; run: () => void } | null>(null);
  const [dating, setDating] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get<{ card: Card }>(`/api/jobs/${jobId}/card`);
      const b = await api.get<BoardContext & { boards: JobBoard[]; exceptions: BoardExceptions }>(
        `/api/jobs/${jobId}/boards`);
      setCard(res.card);
      setBoards(b.boards);
      setBoardExceptions(b.exceptions);
      setBoardCtx(b);
      // A panel still open keeps what is being typed in it; every other one
      // starts again from the card as saved.
      const d = cardDrafts(res.card, b.boards);
      const keep = openRef.current;
      setF((p) => ({
        ...d.f,
        ...Object.fromEntries([...keep].flatMap((s) => SECTION_FIELDS[s].map((k) => [k, p[k]]))),
      }));
      if (!keep.has('route')) { setRouteIds(d.routeIds); setKind(res.card.job_work ? null : typeOfMake(res.card.make).key); }
      if (!keep.has('qty')) {
        setQty(d.qty); setUpsDraft(d.upsDraft); setBoardDraft(d.boardDraft);
        runTyped.current = false;
      }
      if (!keep.has('corrugation')) setLiners(d.liners);
      return res.card;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the job card');
      return null;
    }
  }, [jobId]);

  useEffect(() => { void load(); }, [load]);

  // Opal's own board for an ordinary line; the customer's board for a line they supply the material for.
  const boardOwner = card?.customer_material && card.customer_id ? `?owner=${card.customer_id}` : '';
  useEffect(() => {
    if (!card) return;
    api.get<{ items: BoardOption[] }>(`/api/stock/board-items${boardOwner}`)
      .then((r) => setBoardOptions(r.items)).catch(() => {});
  }, [Boolean(card), boardOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  /** What a fix on this card achieved, for the list it came from. */
  const savedMessage = (c: Card) => {
    const which = part === 'CORRUGATION' ? 'corrugation' : part === 'PRINTING' ? 'printing' : null;
    if (!which) return `${c.job_number} card saved`;
    const done = which === 'corrugation' ? c.corrugation_ready : c.printing_ready;
    return done ? `${c.job_number} ${which} card complete ✓` : `${c.job_number} ${which} card saved — still pending`;
  };

  const boxRefs = {
    qty: useRef<HTMLElement>(null), route: useRef<HTMLElement>(null), printing: useRef<HTMLElement>(null),
    corrugation: useRef<HTMLElement>(null), notes: useRef<HTMLElement>(null),
  };

  useEffect(() => {
    api.get<{ stages: Stage[] }>('/api/reference/stages')
      .then((r) => setStages(r.stages))
      .catch(() => setError('Could not load the production stages'));
  }, []);

  // A number field that was cleared must send null, not 0 -- otherwise
  // emptying "sheet count" would silently record zero sheets.
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
  const textOrNull = (v: string) => (v.trim() === '' ? null : v.trim());

  const isDraft = card?.job_status === 'DRAFT';
  const canDate = isManager && !['CANCELLED', 'DISPATCHED'].includes(card?.job_status ?? '');
  const canSplit = (user.permissions?.includes('prepress.stage') ?? false)
    && isDraft && Number(card?.ordered_quantity ?? 0) > 1;
  const canAddStage = (user.permissions?.includes('production.add_stage') ?? false)
    && ['CREATED', 'IN_PRODUCTION', 'ON_HOLD'].includes(card?.job_status ?? '');
  const canUndo = (user.permissions?.includes('production.record') ?? false)
    && ['IN_PRODUCTION', 'ON_HOLD', 'COMPLETED'].includes(card?.job_status ?? '');
  const canUndoRelease = (user.permissions?.includes('prepress.release') ?? false)
    && card?.job_status === 'CREATED' && card.route.every((r) => r.status === 'PENDING');

  const editingQty = open.has('qty') && isDraft;
  const upsNow = editingQty ? upsOf(upsDraft) : (card?.ups ?? null);
  const upsChanged = editingQty && upsNow !== null && upsNow !== (card?.ups ?? null);
  const splitLine = (card?.line_jobs ?? 1) > 1;
  const savedRun = card?.production_quantity;

  useEffect(() => {
    if (!editingQty || !splitLine) { setLinePlan(null); return; }
    if (!upsChanged) {
      setLinePlan(null);
      if (!runTyped.current && savedRun != null) setQty((q) => ({ ...q, production: numStr(savedRun) }));
      return;
    }
    const t = setTimeout(() => {
      api.get<{ allowed: boolean; runs: UpsRun[] }>(`/api/jobs/${jobId}/ups?ups=${upsNow}`)
        .then((p) => {
          if (!p.allowed) return;
          setLinePlan(p.runs);
          const own = p.runs.find((r) => r.jobId === jobId);
          if (own && !runTyped.current) setQty((q) => ({ ...q, production: String(own.run) }));
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [editingQty, splitLine, upsChanged, upsNow, savedRun, jobId]);

  const sheetsNow = editingQty && upsNow && Number(qty.production) > 0
    ? Math.ceil(Number(qty.production) / upsNow) : null;
  const lastSheets = useRef<number | null>(null);
  useEffect(() => {
    if (!editingQty) { lastSheets.current = null; return; }
    if (sheetsNow === null) return;
    const was = lastSheets.current;
    lastSheets.current = sheetsNow;
    if (was === null || was === sheetsNow) return;
    setBoardDraft((d) => d.map((r) => (r.itemId && Number(r.sheets) === was ? { ...r, sheets: String(sheetsNow) } : r)));
  }, [sheetsNow, editingQty]);

  async function addStage() {
    if (!adding || !card) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await api.post(`/api/jobs/${jobId}/stages`, {
        stageId: Number(adding.stageId), reason: adding.reason.trim(),
      });
      const name = stages.find((st) => st.id === Number(adding.stageId))?.name ?? 'Stage';
      setAdding(null);
      await load();
      setNotice(`${name} added to ${card.job_number}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the stage');
    } finally { setBusy(false); }
  }

  /*
   * Printing records each sheet first (086), so the paper carries what was
   * recorded: a draft prints as a proof; after release a sheet's first print
   * is Rev 1, the same sheet again stays, and one printed after a change is
   * the next revision.
   */
  async function printCard(parts: CardPart[]) {
    setError(''); setPrintMenu(false);
    setPrintParts(parts);
    let made: PrintStamp | null = null;
    try {
      made = await api.post<PrintStamp>(`/api/jobs/${jobId}/card/printed`, { parts });
      await load();
    } catch { /* print regardless: the paper matters more than the record */ }
    setStamp(made);
    // The title becomes the file name when the print is saved as a PDF.
    const was = document.title;
    const which = parts.length === 2 ? 'Job card' : parts[0] === 'PRINTING' ? 'Printing card' : 'Corrugation card';
    const revs = [...new Set(made?.sheets.map((s) => s.revision) ?? [])];
    const tail = made?.proof ? ' DRAFT' : revs.length === 1 && revs[0] ? ` Rev ${revs[0]}` : '';
    setTimeout(() => {
      document.title = `${card?.job_number ?? 'Job'} ${which}${tail}`;
      window.print();
      document.title = was;
    }, 50);
  }

  async function openHistory() {
    try {
      setHistory((await api.get<{ prints: PrintRow[] }>(`/api/jobs/${jobId}/card/prints`)).prints);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the print history');
    }
  }

  // The order line said no board; this box does use it.
  async function markNeedsBoard() {
    if (!card) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await api.patch(`/api/po-items/${card.po_item_id}`, { boardRequired: true });
      setNotice(isDraft ? 'Marked as needing board — choose it with Edit' : 'Marked as needing board');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the order line');
    } finally { setBusy(false); }
  }

  // A board put on a released card, issued off stock in the same step.
  async function addBoard(itemId: number, sheets: number): Promise<boolean> {
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await api.post<{ board: string; sheets: number }>(
        `/api/jobs/${jobId}/boards/add`, { itemId, sheets });
      setNotice(`${fmtN(r.sheets)} sheets of ${r.board} issued to ${card?.job_number ?? 'the job'}`);
      await load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the board');
      return false;
    } finally { setBusy(false); }
  }

  // Typed sheets the run moved under: take the worked-out figure.
  async function applyRunSheets(itemId: number) {
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await api.post<{ sheets: number }>(`/api/jobs/${jobId}/boards/${itemId}/use-run`, {});
      setNotice(`Sheets set to ${fmtN(r.sheets)}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the sheets');
    } finally { setBusy(false); }
  }

  // Released: issue the rest, or return the extra, so issued matches the card.
  async function settleBoard(b: JobBoard) {
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await api.post<{ type: 'ISSUE' | 'RETURN'; sheets: number }>(
        `/api/jobs/${jobId}/boards/${b.item_id}/settle`, {});
      setNotice(r.type === 'ISSUE'
        ? `${fmtN(r.sheets)} more sheets of ${b.item_name} issued`
        : `${fmtN(r.sheets)} sheets of ${b.item_name} returned to stock`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the sheets');
    } finally { setBusy(false); }
  }

  // The ply the layers follow: the box type's when it states one, else the card's.
  const plyNow = card?.box_ply ?? (f.ply.trim() ? Number(f.ply) : null);
  /** Where a new layer starts: the running size and the sheets (one layer per sheet), the box type's flute. */
  const layerStart = () => ({
    size: f.runningSize || str(card?.running_size),
    qty: numStr(card?.boards[0]?.sheets ?? card?.board_sheet_count),
    flute: fluteOfBoxType(card?.box_type),
  });
  // A new ply or kraft printing lists its layers; what was typed stays at its layer.
  const setLayers = (ply: number | null, kraftPrinting: boolean) => {
    setF((p) => ({ ...p, ply: ply === null ? '' : String(ply), kraftPrinting }));
    setLiners((rows) => layersFor(ply, kraftPrinting, rows, layerStart()));
  };

  function openSection(which: Section) {
    setNotice(''); setNoticeUndo(null); setError('');
    if (which === 'corrugation') setLiners((rows) => layersFor(plyNow, f.kraftPrinting, rows, layerStart()));
    setOpen(new Set(openRef.current).add(which));
  }

  const closeSection = (which: Section) => {
    const next = new Set(openRef.current);
    next.delete(which);
    setOpen(next);
  };

  // Puts this panel back to the card as saved; the other open panels keep their typing.
  function cancelSection(which: Section) {
    closeSection(which); setError('');
    void load();
  }

  async function saveSection(section: Section) {
    if (!card) return;
    setBusy(true); setError('');
    const stop = (msg: string) => { setError(msg); setBusy(false); };
    try {
      if (section === 'qty') {
        if (card.board_required && !card.ups_fixed_by && (upsDraft.trim() !== '' || card.ups) && upsNow === null) {
          stop('Enter the ups as a whole number — boxes on one sheet'); return;
        }
        const rows = boardDraft.filter((r) => r.itemId);
        if (rows.some((r) => !(Number(r.sheets) > 0) || !Number.isInteger(Number(r.sheets)))) {
          stop('Enter the number of sheets for each board on the card'); return;
        }
        if (upsChanged) await api.put(`/api/jobs/${jobId}/ups`, { ups: upsNow });
        await api.patch(`/api/jobs/${jobId}/card`, {
          orderedQuantity: Number(qty.ordered),
          productionQuantity: Number(qty.production || qty.ordered),
        });
        await api.put(`/api/jobs/${jobId}/boards`, {
          boards: rows.map((r) => ({ itemId: Number(r.itemId), sheets: Number(r.sheets) })),
        });
      } else if (section === 'route') {
        if (!stages.some((st) => routeIds.includes(st.id) && !st.always_included)) {
          stop('Choose at least one production stage for the route'); return;
        }
        await api.put(`/api/jobs/${jobId}/route`, {
          stageIds: stages.filter((st) => routeIds.includes(st.id)).map((st) => st.id),
          ...(kind !== null ? { make: boxType(kind)?.make ?? null } : {}),
        });
        resetJobMakes();
      } else if (section === 'printing') {
        if (f.plateType === 'OLD' && !f.plateSetNumber.trim()) {
          stop('Enter the plate set number — an old plate has to be found on the rack'); return;
        }
        await api.patch(`/api/jobs/${jobId}/card`, {
          plateType: f.plateType === '' ? null : f.plateType,
          plateSetNumber: textOrNull(f.plateSetNumber),
          plateSave: f.plateSave,
          colour: textOrNull(f.colour),
          printingSide: textOrNull(f.printingSide),
          runningSize: textOrNull(f.runningSize),
          finishingWork: textOrNull(f.finishingWork),
        });
      } else if (section === 'corrugation') {
        // An old card's even ply goes back unchanged, so it is only sent when chosen anew.
        const ply = numOrNull(f.ply);
        await api.patch(`/api/jobs/${jobId}/card`, {
          ...(!card.box_ply && ply !== card.ply ? { ply } : {}),
          kraftPrinting: f.kraftPrinting,
        });
        await api.put(`/api/jobs/${jobId}/liners`, { liners: layersBody(plyNow, f.kraftPrinting, liners) });
      } else {
        await api.patch(`/api/jobs/${jobId}/card`, { instructions: textOrNull(f.instructions) });
      }
      closeSection(section);
      savedThisVisit.current = true;
      await load();
      setNotice(`${SECTION_NAME[section]} saved`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function uploadArtwork(file: File) {
    setBusy(true); setError('');
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`/api/jobs/${jobId}/artwork`, {
        method: 'POST', credentials: 'same-origin', body,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message ?? 'Upload failed');
      }
      setNotice('Artwork uploaded');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload the artwork');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeArtwork() {
    if (!window.confirm('Remove the artwork from this job card?')) return;
    setBusy(true); setError('');
    try {
      await fetch(`/api/jobs/${jobId}/artwork`, { method: 'DELETE', credentials: 'same-origin' });
      setNotice('Artwork removed');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the artwork');
    } finally {
      setBusy(false);
    }
  }

  if (!card) {
    return (
      <div className="panel">
        {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
        {!error && <div className="empty">Loading job card…</div>}
      </div>
    );
  }

  // Cache-busted so a freshly replaced image is not served from cache.
  const artworkSrc = card.has_artwork
    ? `/api/jobs/${jobId}/artwork?v=${encodeURIComponent(card.artwork_name ?? '1')}`
    : undefined;

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  const wasteSheets = Number(card.waste_allowance_sheets ?? 0);
  const run = (ordered: number) => runFor(ordered, wasteSheets, upsNow);
  const changeUps = (v: string) => {
    setUpsDraft(v);
    if (splitLine) return;
    const next = upsOf(v);
    setQty((p) => (p.production === '' || Number(p.production) === runFor(Number(p.ordered), wasteSheets, upsNow)
      ? { ...p, production: String(runFor(Number(p.ordered), wasteSheets, next)) } : p));
  };

  /*
   * Sections follow the route: a mono carton that skips corrugation has no
   * use for ply and liners. With no route chosen yet, everything shows.
   * Each stage says which card it works from, so a stage added later finds
   * its card without the screen knowing its name.
   */
  const draftRoute = open.has('route') && isDraft;
  const editPrinting = open.has('printing');
  const chosen = stages.filter((st) => routeIds.includes(st.id) && !st.always_included);
  const noRoute = draftRoute
    ? chosen.length === 0
    : card.route.filter((r) => !r.alwaysIncluded).length === 0;
  const uses = (part: CardPart) => noRoute || (draftRoute
    ? chosen.some((st) => st.card_part === part)
    : part === 'PRINTING' ? card.has_printing : card.has_corrugation);

  const missingRules = draftRoute
    ? chosen.map((st) => ({ st, need: stages.filter((n) => st.requires.includes(n.id) && !routeIds.includes(n.id)) }))
        .filter((x) => x.need.length > 0)
    : [];
  const onRoute = new Set(card.route.map((r) => r.stageId));
  const addable = stages.filter((st) => !st.always_included && !onRoute.has(st.id));
  const addedReason = new Map(card.added_stages.map((a) => [a.jobStageId, a.reason]));
  const outsideBy = new Map((card.outside ?? []).map((o) => [o.jobStageId, o]));

  const tabs: CardPart[] = [
    ...(uses('PRINTING') ? ['PRINTING' as const] : []),
    ...(uses('CORRUGATION') ? ['CORRUGATION' as const] : []),
  ];
  const editingCorr = open.has('corrugation');
  // The sheets the floor needs a new paper of (086): released, and either never
  // printed since or changed since the revision they hold.
  const released = !isDraft && card.job_status !== 'CANCELLED';
  const toPrint = (released ? tabs : []).map((part) => ({
    part,
    rev: (part === 'PRINTING' ? card.printing_revision : card.corrugation_revision) ?? null,
    changed: Boolean(part === 'PRINTING' ? card.printing_changed : card.corrugation_changed),
  })).filter((t) => t.rev === null || t.changed);
  const printingReady = editPrinting
    ? f.plateType === 'NEW' || (f.plateType === 'OLD' && f.plateSetNumber.trim() !== '')
    : card.printing_ready;
  // Layer 1 when the face is printed on the card's board.
  const faceBoard: FaceBoard | null = card.boards[0]
    ? { name: card.boards[0].name, size: `${card.boards[0].lengthCm} X ${card.boards[0].widthCm} CM`,
        sheets: card.boards[0].sheets }
    : null;
  const corrugationReady = editingCorr
    ? layersComplete(plyNow, f.kraftPrinting, liners)
    : card.corrugation_ready;
  const canEditCorr = isManager && card.corrugation_editable && tabs.includes('CORRUGATION');
  const corrugationRequired = draftRoute
    ? noRoute || chosen.some((st) => st.card_part === 'CORRUGATION' && st.card_required)
    : card.corrugation_required;

  const firstOption = boardOptions.find((o) => String(o.item_id) === boardDraft[0]?.itemId);
  const firstBoardSize: [number, number] | null = editingQty
    ? (firstOption ? parseSize(`${firstOption.length_cm}x${firstOption.width_cm}`) : null)
    : (card.boards[0] ? parseSize(`${card.boards[0].lengthCm}x${card.boards[0].widthCm}`) : null);

  // Edit stays on every panel; only the job's own rules close one (quantity,
  // board and route fixed at release; the card locked once production starts).
  const may = (which: Section) => !open.has(which) && (
    which === 'qty' || which === 'route' ? canEdit && isDraft
      : which === 'corrugation' ? canEdit || canEditCorr
        : canEdit);
  const boxProps = (which: Section) => ({
    editing: open.has(which), busy, boxRef: boxRefs[which],
    onEdit: may(which) ? () => openSection(which) : null,
    onCancel: () => cancelSection(which), onSave: () => void saveSection(which),
  });

  // Typing not yet saved: Print would put the old card on paper, Split would
  // copy it, and Back would lose it.
  const savedDrafts = cardDrafts(card, boards);
  const nowDrafts: Drafts = { f, routeIds, qty, upsDraft, boardDraft, liners };
  const unsaved = [...open].filter((s) =>
    JSON.stringify(sectionDraft(nowDrafts, s)) !== JSON.stringify(sectionDraft(savedDrafts, s)));
  const saveFirst = unsaved.length > 0 ? 'Save or cancel your changes first' : undefined;
  const leave = () => {
    if (unsaved.length > 0 && !window.confirm(
      `${unsaved.map((s) => SECTION_NAME[s]).join(' and ')}: changes not saved. Leave without saving?`)) return;
    onBack(startEditing && savedThisVisit.current ? savedMessage(card) : undefined);
  };

  const stagesOnRoute = card.route.filter((r) => !r.alwaysIncluded).length;
  const shortSheets = boards.reduce((t, b) => t + (b.status === 'SHORT' ? Number(b.short) : 0), 0);
  const checks: { key: Section; state: 'ok' | 'warn' | 'todo' | 'optional'; text: string }[] = [
    stagesOnRoute > 0
      // Every step the job goes through, Packing and Dispatch too, so it
      // matches the numbered route below.
      ? { key: 'route', state: 'ok', text: `Route · ${card.route.length} stages` }
      : { key: 'route', state: 'todo', text: 'Route not chosen' },
    !card.board_required ? { key: 'qty', state: 'ok', text: 'Board not needed' }
      : !card.ups ? { key: 'qty', state: 'todo', text: 'Ups not set' }
        : boards.length === 0 ? { key: 'qty', state: 'todo', text: 'Board not chosen' }
          : shortSheets > 0 ? { key: 'qty', state: 'warn', text: `Board short ${fmtN(shortSheets)}` }
            : { key: 'qty', state: 'ok', text: 'Board available' },
    ...(uses('PRINTING') ? [card.printing_ready
      ? { key: 'printing' as const, state: 'ok' as const, text: 'Printing card' }
      : { key: 'printing' as const, state: 'todo' as const, text: 'Printing card · choose the plate' }] : []),
    ...(uses('CORRUGATION') ? [card.corrugation_ready
      ? { key: 'corrugation' as const, state: 'ok' as const, text: 'Corrugation card' }
      : card.corrugation_required
        ? { key: 'corrugation' as const, state: 'todo' as const, text: 'Corrugation card · ply, GSM and BF' }
        : { key: 'corrugation' as const, state: 'optional' as const, text: 'Corrugation card · optional' }] : []),
  ];
  const [statusLabel, statusTag] = STATUS[card.job_status] ?? [card.job_status, 'blocked'];
  const cardState = (ready: boolean, required: boolean) => (
    <span className={`tag ${ready ? 'ready' : required ? 'waiting' : 'blocked'}`}>
      {ready ? 'Complete' : required ? 'Pending' : 'Optional'}
    </span>
  );

  return (
    <>
      <div className="panel no-print jc-panel">
        <div className="panel-head jc-head">
          <button className="btn" onClick={leave}>Back</button>
          <div className="jcs-title">
            <div className="jcs-title-row">
              <h2>{card.job_number}<MakeTag number={card.job_number} /></h2>
              <span className={`tag ${statusTag}`}>{statusLabel}</span>
              {(card.priority === 'URGENT' || card.priority === 'HIGH') && (
                <span className={`tag ${card.priority === 'URGENT' ? 'urgent' : 'high'}`}>
                  {PRIORITY_LABEL[card.priority]}
                </span>
              )}
              {(card.print_count ?? 0) > 0 && (
                <button className="linkish jcs-hist" onClick={() => void openHistory()}>
                  Printed {card.print_count} {card.print_count === 1 ? 'time' : 'times'}
                </button>
              )}
            </div>
            <div className="jcs-sub">
              {card.customer_name} · {card.item_name}{card.po_number ? ` · PO ${poNo(card.po_number)}` : ''}
            </div>
          </div>
          <div className="spacer" />
          {canSplit && (
            <button className="btn" disabled={Boolean(saveFirst)} title={saveFirst}
                    onClick={() => { setNotice(''); setNoticeUndo(null); setSplitting(true); }}>
              Split…
            </button>
          )}
          {isManager && locked && !canEditCorr && (
            <span className="tag blocked" title="Editing closed once production starts">Locked</span>
          )}
          {tabs.length < 2 ? (
            <button className="btn primary" disabled={Boolean(saveFirst)} title={saveFirst}
                    onClick={() => void printCard(tabs.length ? tabs : ['PRINTING', 'CORRUGATION'])}>
              Print card
            </button>
          ) : (
            <span className="menu-wrap">
              <button className="btn primary" aria-expanded={printMenu} disabled={Boolean(saveFirst)} title={saveFirst}
                      onClick={() => setPrintMenu((v) => !v)}>
                Print ▾
              </button>
              {printMenu && (
                <>
                  <div className="menu-scrim" onClick={() => setPrintMenu(false)} />
                  <div className="menu" role="menu">
                    <button role="menuitem" onClick={() => void printCard(['PRINTING'])}>Printing card</button>
                    <button role="menuitem" onClick={() => void printCard(['CORRUGATION'])}>Corrugation card</button>
                    <button role="menuitem" onClick={() => void printCard(['PRINTING', 'CORRUGATION'])}>Both</button>
                  </div>
                </>
              )}
            </span>
          )}
        </div>

        <div className="jcs-body">
          {error && <div className="error" style={{ marginBottom: 14 }}>{error}</div>}
          {notice && (
            <div style={{ marginBottom: 14 }} className="tag ready notice">
              {notice}
              {noticeUndo?.text === notice && (
                <>{' '}<button className="linkish"
                               onClick={() => { const u = noticeUndo; setNoticeUndo(null); u.run(); }}>
                  Undo
                </button></>
              )}
            </div>
          )}
          {toPrint.length > 0 && !editing && (
            <div className="tag waiting notice jcs-reprint" style={{ marginBottom: 14 }}>
              <span>
                {toPrint.every((t) => t.rev === null) && toPrint.length === tabs.length
                  ? 'Not printed for the floor yet'
                  : toPrint.map((t) => (t.rev === null
                    ? `${SHEET_NAME[t.part]} not printed for the floor yet`
                    : `${SHEET_NAME[t.part]} changed since Rev ${t.rev} was printed`)).join(' · ')}
              </span>
              <button className="btn primary" disabled={Boolean(saveFirst)} title={saveFirst}
                      onClick={() => void printCard(toPrint.map((t) => t.part))}>
                {toPrint.length === 1 ? `Print Rev ${(toPrint[0]!.rev ?? 0) + 1}` : 'Print both'}
              </button>
            </div>
          )}
          {locked && (
            <div className="hint" style={{ margin: '0 0 14px' }}>
              Locked — {card.job_number} is {card.job_status.toLowerCase().replace('_', ' ')}, and the
              floor works from the printed copy.
              {canEditCorr && ' The corrugation card stays open until Corrugation starts.'}
            </div>
          )}

          {isDraft && (
            <div className="jcs-ready" aria-label="Before release">
              {checks.map((c) => (
                <button key={c.key} className={`jcs-check ${c.state}`}
                        onClick={() => boxRefs[c.key].current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                  <span className="ic">{c.state === 'ok' ? '✓' : c.state === 'warn' ? '!' : c.state === 'optional' ? '–' : '✕'}</span>
                  {c.text}
                </button>
              ))}
            </div>
          )}

          <div className="jcs-layout">
            <div className="jcs-main">
              <Box title="Quantity and board" {...boxProps('qty')}>
                <div className="jcs-halves">
                  <div>
                    <div className="jcs-grid3">
                      <Field label="Quantity ordered">
                        {editingQty
                          ? <input type="number" min="1" value={qty.ordered}
                                   onChange={(e) => {
                                     const ordered = e.target.value;
                                     setQty((p) => ({
                                       ordered,
                                       production: p.production === ''
                                         || Number(p.production) === run(Number(p.ordered))
                                         ? String(run(Number(ordered))) : p.production,
                                     }));
                                   }} />
                          : <Read v={fmt(card.ordered_quantity)} />}
                      </Field>
                      {card.board_required && (
                        <Field label="Ups">
                          {editingQty && !card.ups_fixed_by
                            ? <input type="number" min="1" step="1" value={upsDraft} placeholder="Boxes on one sheet"
                                     onChange={(e) => changeUps(e.target.value)} />
                            : card.ups
                              ? <Read v={card.ups} />
                              : <div style={{ padding: '6px 0' }}><span className="tag waiting">Not set</span></div>}
                          {editingQty && (
                            <div className="hint">
                              {card.ups_fixed_by ? `Fixed · ${card.ups_fixed_by} has gone to production` : 'Boxes on one sheet'}
                            </div>
                          )}
                        </Field>
                      )}
                      <Field label="Quantity to run">
                        {editingQty
                          ? <input type="number" min="1" value={qty.production}
                                   onChange={(e) => { runTyped.current = true; setQty((p) => ({ ...p, production: e.target.value })); }} />
                          : <Read v={fmt(card.production_quantity)} />}
                      </Field>
                    </div>
                    {editingQty && Number(qty.ordered) > 0 && (
                      <div className="hint">
                        {Number(qty.production) > 0 && Number(qty.production) < Number(qty.ordered) ? (
                          <span className="run-short">
                            Run at least {fmtN(Number(qty.ordered))}, the quantity ordered.
                            {canSplit ? ' To make fewer now, use Split.' : ''}
                          </span>
                        ) : !upsNow
                          ? (card.board_required ? `Enter the ups to add the ${fmtN(wasteSheets)} waste boards` : null)
                          : splitLine
                            ? (Number(qty.production) > Number(qty.ordered)
                                ? `Run = order + ${fmtN(Number(qty.production) - Number(qty.ordered))} spare, this part's share of the ${fmtN(wasteSheets)} waste boards`
                                : `No spare on this part · the ${fmtN(wasteSheets)} waste boards went with the other parts`)
                            : `Run = order + ${fmtN(wasteSheets)} waste boards × ${upsNow} ups`}
                        {upsNow && !splitLine && Number(qty.production) !== run(Number(qty.ordered)) && (
                          <>{' · '}{fmtN(run(Number(qty.ordered)))}{' '}
                            <button className="linkish"
                                    onClick={() => { runTyped.current = false; setQty((p) => ({
                                      ...p, production: String(run(Number(p.ordered))) })); }}>
                              Use
                            </button>
                          </>
                        )}
                        {linePlan && linePlan.filter((r) => r.jobId !== jobId).map((r) => (
                          <div key={r.jobId}>Saving also sets {r.jobNumber} to run {fmtN(r.run)} · {fmtN(r.sheets)} sheets</div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <BoardSection
                      editing={editingQty}
                      live={editingQty ? {
                        suggested: sheetsNow,
                        why: sheetsNow && upsNow ? `${fmtN(Number(qty.production))} to run ÷ ${upsNow} ups` : null,
                      } : null}
                      boards={boards} ctx={boardCtx} draft={boardDraft} setDraft={setBoardDraft}
                      options={boardOptions} boardRequired={card.board_required}
                      canAct={isManager} acting={busy} onUseRun={applyRunSheets} onSettle={settleBoard}
                      onNeedsBoard={() => void markNeedsBoard()} onAddBoard={addBoard} />
                    {card.customer_material && boardOptions.length === 0 && (
                      <div className="hint" style={{ marginTop: 8 }}>
                        No board from {card.customer_name} yet. Stock it in under Stock › Board › Customer's.
                      </div>
                    )}
                    {boardExceptions && <BoardExceptionNotes x={boardExceptions} />}
                  </div>
                </div>
              </Box>

              <Box title="Route" {...boxProps('route')}>
                <Field label={draftRoute ? 'Untick anything this box does not need' : ''}>
                  {draftRoute ? (
                    <>
                      {card.job_work ? (
                        <div className="kind-pick" role="group" aria-label="Job work">
                          <button type="button" aria-pressed className="on">{JOB_WORK_LABEL}</button>
                          <span className="dim kind-note">Differs each time · tick whatever this job needs below</span>
                        </div>
                      ) : (
                      <div className="kind-pick" role="group" aria-label="Box type">
                        {BOX_TYPES.map((t) => (
                          <button key={t.key} type="button" aria-pressed={kind === t.key}
                                  className={kind === t.key ? 'on' : undefined}
                                  onClick={() => {
                                    setKind(t.key);
                                    setRouteIds(stages.filter((st) => t.tick.includes(st.name)).map((st) => st.id));
                                  }}>
                            {t.label}
                          </button>
                        ))}
                        {boxType(kind)?.note && <span className="dim kind-note">{boxType(kind)!.note}</span>}
                      </div>
                      )}
                      <div className="route-pick">
                        {/* Packing and dispatch are left out on purpose: every box
                            gets both, so offering them as choices only invites
                            somebody to untick one by accident. The server appends
                            them regardless of what is sent. */}
                        {stages.filter((st) => !st.always_included).map((st) => (
                          <label key={st.id}>
                            <input type="checkbox" checked={routeIds.includes(st.id)}
                                   onChange={() => setRouteIds((p) =>
                                     p.includes(st.id) ? p.filter((x) => x !== st.id) : [...p, st.id])} />
                            {st.name}
                          </label>
                        ))}
                      </div>
                      {missingRules.map(({ st, need }) => (
                        <div key={st.id} className="error" style={{ marginTop: 8 }}>
                          {st.name} needs {need.map((n) => n.name).join(' and ')} before it.{' '}
                          <button className="linkish"
                                  onClick={() => setRouteIds((p) => [...p, ...need.map((n) => n.id)])}>
                            Tick {need.length === 1 ? need[0]!.name : 'them'}
                          </button>
                        </div>
                      ))}
                      <div className="route-preview">
                        <span className="dim">Route</span>
                        {chosen.length === 0
                          ? <span className="step none">Nothing ticked yet</span>
                          : chosen.map((st) => <span key={st.id} className="step">{st.name}</span>)}
                        {stages.filter((st) => st.always_included).map((st) => (
                          <span key={st.id} className="step fixed" title="On every card">{st.name}</span>
                        ))}
                      </div>
                      <div className="hint" style={{ marginTop: 6 }}>
                        Runs in factory order. {stages.filter((st) => st.always_included).map((st) => st.name).join(' and ')}
                        {' '}are on every card. A stage that is not listed is added in Settings → Production route.
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '2px 0' }}>
                      {card.route.length === 0
                        ? <span className="dim">No route chosen yet</span>
                        : (
                          <ol className="jcs-route">
                            {card.route.map((r, i) => {
                              const added = addedReason.has(r.jobStageId);
                              return (
                                <li key={r.jobStageId}
                                    className={`jcs-step ${r.status.toLowerCase()}${r.alwaysIncluded ? ' fixed' : ''}${added ? ' added' : ''}`}
                                    title={added ? `Added after release: ${addedReason.get(r.jobStageId) ?? ''}`
                                      : r.alwaysIncluded ? 'On every card' : undefined}>
                                  <span className="n">{r.status === 'COMPLETED' ? '✓' : i + 1}</span>
                                  {r.name}
                                  {r.status !== 'PENDING' && r.status !== 'COMPLETED' && (
                                    <span className="st"> · {r.status.toLowerCase().replace('_', ' ')}</span>
                                  )}
                                  {added && <span className="st"> · added</span>}
                                  {outsideBy.get(r.jobStageId) && (() => {
                                    const o = outsideBy.get(r.jobStageId)!;
                                    return (
                                      <span className="tag waiting" style={{ marginLeft: 6 }}
                                            title={o.status === 'OUT' ? `Out with ${o.worker}` : `Planned to go to ${o.worker}`}>
                                        {o.status === 'OUT' ? 'At' : 'Outside ·'} {o.worker}
                                        {o.quantity ? ` · ${fmtN(o.quantity)}` : ''}
                                      </span>
                                    );
                                  })()}
                                  {added && r.status === 'PENDING' && canUndo && (
                                    <button className="linkish" style={{ marginLeft: 6 }} title="Take this added stage off the route"
                                            onClick={() => setUndoUrl(`/api/production/job-stages/${r.jobStageId}/undo`)}>
                                      ×
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      {canUndo && card.route.some((r) => r.status !== 'PENDING') && (
                        <button className="linkish" style={{ marginLeft: 4, marginRight: 8 }} disabled={busy}
                                onClick={() => setUndoUrl(`/api/production/jobs/${jobId}/undo`)}>
                          Undo last step
                        </button>
                      )}
                      {canUndoRelease && (
                        <button className="linkish" style={{ marginLeft: 4, marginRight: 8 }} disabled={busy}
                                onClick={() => setUndoUrl(`/api/production/jobs/${jobId}/undo`)}>
                          Undo release
                        </button>
                      )}
                      {canAddStage && addable.length > 0 && (
                        <button className="linkish" style={{ marginLeft: 4 }} disabled={busy}
                                onClick={() => { setNotice(''); setAdding({ stageId: String(addable[0]!.id), reason: '' }); }}>
                          + Add a stage
                        </button>
                      )}
                    </div>
                  )}
                </Field>
                {draftRoute && card.route.length === 0 && card.suggested_route.length > 0 && (
                  <div className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
                    Ticked from {card.suggested_route_from}, this box's last card. Untick anything this
                    job does not need, then save.
                  </div>
                )}
                {!isDraft && card.card_editable && (
                  <div className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
                    Released — route and quantities are fixed.
                  </div>
                )}
                {card.added_stages.length > 0 && (
                  <div className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
                    {card.added_stages.map((a) => (
                      <div key={a.jobStageId}>
                        {card.route.find((r) => r.jobStageId === a.jobStageId)?.name} added after release
                        {a.reason ? ` — ${a.reason}` : ''}
                      </div>
                    ))}
                  </div>
                )}


              </Box>

              <div className="jcs-pair">
                {uses('PRINTING') && (
                  <Box title="Printing card" status={cardState(printingReady, true)} {...boxProps('printing')}>
                    <div className="jcs-grid3">
                      <Field label="Plate">
                        {editPrinting ? (
                          <select value={f.plateType} onChange={set('plateType')}>
                            <option value="">—</option>
                            <option value="OLD">OLD</option>
                            <option value="NEW">NEW</option>
                          </select>
                        ) : (
                          // Plate save rides on the plate, as on the printed card: a badge when set, nothing when not.
                          <div className={`jcs-val${card.plate_type ? '' : ' dim'}`}>
                            {card.plate_type || 'Not set'}
                            {card.plate_save && <span className="jcs-badge">Plate save</span>}
                          </div>
                        )}
                      </Field>
                      <Field label="Plate set no." req={editPrinting && f.plateType === 'OLD'}>
                        {editPrinting
                          ? <input type="text" value={f.plateSetNumber} onChange={set('plateSetNumber')}
                                   placeholder={f.plateType === 'OLD' ? 'Which set to pull from the rack' : ''} />
                          : <Read v={card.plate_set_number} />}
                      </Field>
                      <Field label="Colour">
                        {editPrinting
                          ? <OptionInput list="colour" value={f.colour}
                                         onChange={(v) => setF((p) => ({ ...p, colour: v }))} />
                          : <Read v={card.colour} />}
                      </Field>
                      <Field label="Printing side">
                        {editPrinting
                          ? <OptionInput list="printing_side" value={f.printingSide}
                                         onChange={(v) => setF((p) => ({ ...p, printingSide: v }))} />
                          : <Read v={card.printing_side} />}
                      </Field>
                      <Field label="Running size">
                        {editPrinting
                          ? <OptionInput list="running_size" value={f.runningSize}
                                         onChange={(v) => setF((p) => ({ ...p, runningSize: v }))} />
                          : <Read v={card.running_size} />}
                        <RunningSizeHint running={editPrinting ? f.runningSize : card.running_size} board={firstBoardSize}
                                         onUse={editPrinting ? (v) => setF((p) => ({ ...p, runningSize: v })) : undefined} />
                      </Field>
                      <Field label="Finishing work">
                        {editPrinting
                          ? <OptionInput list="finishing_work" value={f.finishingWork}
                                         onChange={(v) => setF((p) => ({ ...p, finishingWork: v }))} />
                          : <Read v={card.finishing_work} />}
                      </Field>
                      {editPrinting && (
                        <Field label="Plate save">
                          <label style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 6 }}>
                            <input type="checkbox" checked={f.plateSave}
                                   onChange={(e) => setF((p) => ({ ...p, plateSave: e.target.checked }))} />
                            Print the PLATE SAVE badge
                          </label>
                        </Field>
                      )}
                    </div>
                  </Box>
                )}

                {uses('CORRUGATION') && (
                  <Box title="Corrugation card" status={cardState(corrugationReady, corrugationRequired)} {...boxProps('corrugation')}>
                    {editingCorr ? (
                      <>
                        <PlyPicker ply={plyNow} boxPly={card.box_ply} kraftPrinting={f.kraftPrinting} make={card.make}
                                   onPly={(v) => setLayers(v, f.kraftPrinting)}
                                   onKraft={(on) => setLayers(plyNow, on)} />
                        {plyNow === null ? (
                          <div className="hint">Choose the ply to list its layers.</div>
                        ) : plyNow === 1 ? (
                          <div className="hint ly-missing">{ONE_PLY}</div>
                        ) : (
                          <LayersEditor rows={liners} kraftPrinting={f.kraftPrinting} board={faceBoard}
                                        onChange={setLiners} />
                        )}
                      </>
                    ) : (
                      <>
                        <div className="jcs-grid3">
                          <Field label="Ply">
                            <div className={`jcs-val${card.box_ply ?? card.ply ? '' : ' dim'}`}>
                              {card.box_ply ?? card.ply ? plyName(Number(card.box_ply ?? card.ply)) : 'Not set'}
                              {card.kraft_printing && (card.make === 'KRAFT_PRINT' || !card.make) && <span className="jcs-badge">Kraft printing</span>}
                            </div>
                          </Field>
                          <Field label="Box size">
                            <Read v={card.box_size} />
                          </Field>
                        </div>
                        {(card.box_ply ?? card.ply) === 1 ? (
                          <div className="hint ly-missing">{ONE_PLY}</div>
                        ) : (card.box_ply ?? card.ply) === null && card.liners.length === 0 ? (
                          <div className="hint">No ply yet. The card prints three blank rows.</div>
                        ) : (
                          <LayersView ply={card.box_ply ?? card.ply} kraftPrinting={Boolean(card.kraft_printing)}
                                      rows={card.liners} board={faceBoard} />
                        )}
                      </>
                    )}
                  </Box>
                )}
              </div>
            </div>

            <aside className="jcs-side">
              <Box title="Order" busy={busy} editing={false} onEdit={null} onCancel={() => {}} onSave={() => {}}>
                <dl className="jcs-facts">
                  <dt>Customer PO</dt><dd>{card.po_number ? poNo(card.po_number) : <span className="dim">None</span>}</dd>
                  <dt>Ordered on</dt><dd>{fmtDate(card.po_date)}</dd>
                  <dt>Delivery</dt>
                  <dd>
                    {card.delivery_date ? fmtDate(card.delivery_date) : <span className="dim">Not set</span>}
                    {canDate && (
                      <>{' '}<button className="linkish" onClick={() => { setNotice(''); setNoticeUndo(null); setDating(true); }}>
                        Change
                      </button></>
                    )}
                    {card.own_delivery_date && (
                      <div className="hint">Own date · order line {fmtDate(card.line_delivery_date ?? null)}</div>
                    )}
                  </dd>
                  <dt>Priority</dt><dd>{PRIORITY_LABEL[card.priority] ?? card.priority}</dd>
                </dl>
              </Box>

              <Box title="Instruction" {...boxProps('notes')}>
                {open.has('notes')
                  ? <input type="text" autoFocus value={f.instructions} onChange={set('instructions')}
                           placeholder="Printed on both cards" />
                  : card.instructions
                    ? <div>{card.instructions}</div>
                    : <div className="dim">None · printed on both cards</div>}
              </Box>

              {uses('PRINTING') && (
                <Box title="Artwork" busy={busy} editing={false} onEdit={null} onCancel={() => {}} onSave={() => {}}>
                  <div className="jcs-art">
                    {card.has_artwork ? (
                      <>
                        <a className="jcs-art-thumb" href={artworkSrc} target="_blank" rel="noreferrer"
                           title="Open full size">
                          <img src={artworkSrc} alt="Job card artwork" />
                        </a>
                        <div className="jcs-art-info">
                          <div className="jcs-art-name" title={card.artwork_name ?? undefined}>{card.artwork_name}</div>
                          <div className="hint">
                            Uploaded {fmtDate(card.artwork_at ? String(card.artwork_at).slice(0, 10) : null)}
                          </div>
                          {canEdit && (
                            <div className="jcs-art-acts">
                              <button className="linkish" disabled={busy}
                                      onClick={() => fileInput.current?.click()}>Replace</button>
                              <button className="linkish" disabled={busy} onClick={removeArtwork}>Remove</button>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="jcs-art-info">
                          <div>No artwork yet</div>
                          <div className="hint">Prints on the printing card · up to 5 MB</div>
                        </div>
                        {canEdit && (
                          <button className="btn" disabled={busy} onClick={() => fileInput.current?.click()}>
                            Upload
                          </button>
                        )}
                      </>
                    )}
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp"
                           style={{ display: 'none' }}
                           onChange={(e) => {
                             const file = e.target.files?.[0];
                             if (file) void uploadArtwork(file);
                           }} />
                  </div>
                </Box>
              )}
            </aside>
          </div>
        </div>
      </div>

      {undoUrl && (
        <UndoDialog previewUrl={undoUrl}
                    onClose={() => setUndoUrl(null)}
                    onDone={(msg) => { setNotice(msg); void load(); }} />
      )}

      {dating && (
        <DeliveryDateDialog job={card}
                            onClose={() => setDating(false)}
                            onDone={(r, undo) => {
                              const msg = `${r.jobNumber} delivery ${fmtDate(r.deliveryDate)}`;
                              setNotice(msg);
                              setNoticeUndo({
                                text: msg,
                                run: () => {
                                  void api.put<{ deliveryDate: string | null }>(`/api/jobs/${card.job_id}/delivery-date`, { deliveryDate: undo })
                                    .then((b) => { setNotice(`${r.jobNumber} delivery back to ${fmtDate(b.deliveryDate)}`); void load(); })
                                    .catch((e) => setError(e instanceof Error ? e.message : 'Could not undo'));
                                },
                              });
                              void load();
                            }} />
      )}

      {splitting && (
        <SplitDialog row={{
                       job_id: card.job_id, job_number: card.job_number, ordered_quantity: card.ordered_quantity,
                       customer_name: card.customer_name, box_name: card.item_name,
                       is_staged: Boolean(card.is_staged), has_card: Boolean(card.card_made_at),
                       board_paired: String(boards.reduce((t, b) => t + Number(b.paired), 0)),
                       sheets_by_hand: boards.some((b) => b.set_by_hand),
                     }}
                     onClose={() => setSplitting(false)}
                     onDone={(msg, splitId) => {
                       setNotice(msg);
                       setNoticeUndo({ text: msg, run: () => setUndoUrl(`/api/pre-press/splits/${splitId}/undo`) });
                       void load();
                     }} />
      )}

      {adding && (
        <ConfirmDialog
          title={`Add a stage to ${card.job_number}`}
          subtitle="It goes in by factory order, never before work that has already started."
          confirmLabel="Add stage" cancelLabel="Cancel" busy={busy} focusConfirm={false}
          confirmDisabled={!adding.reason.trim()}
          onConfirm={() => void addStage()}
          onCancel={() => setAdding(null)}>
          <div className="field">
            <label className="req">Stage</label>
            <select value={adding.stageId}
                    onChange={(e) => setAdding({ ...adding, stageId: e.target.value })}>
              {addable.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="req">Reason</label>
            <input type="text" maxLength={300} autoFocus placeholder="Customer asked for varnish"
                   value={adding.reason}
                   onChange={(e) => setAdding({ ...adding, reason: e.target.value })} />
            <div className="hint" style={{ marginTop: 6 }}>Shown on the card next to the stage.</div>
          </div>
        </ConfirmDialog>
      )}

      {history && (
        <div className="backdrop" onClick={() => setHistory(null)}>
          <div className="modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Print history · {card.job_number}</h3>
              <p>Every sheet printed, newest first. A draft's print is a proof and carries no revision.</p>
            </div>
            <div className="modal-body">
              <div className="tbl">
                <table className="fit">
                  <thead><tr><th>Printed</th><th>Sheet</th><th>Copy</th><th>By</th></tr></thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id}>
                        <td>{fmtStamp(h.printed_at)}</td>
                        <td>{SHEET_NAME[h.part]}</td>
                        <td>{h.revision ? `Rev ${h.revision}` : <span className="dim">Draft proof</span>}</td>
                        <td>{h.printed_by ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setHistory(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <JobCardPrint card={card} artworkSrc={artworkSrc} stamp={stamp}
                    parts={printParts.length ? printParts : tabs.length ? tabs : ['PRINTING', 'CORRUGATION']} />
    </>
  );
}

/**
 * The board this job needs, from Board stock (053). Nothing is held when the
 * card is saved: the job that goes to production first takes the sheets, at
 * release. Sheets are worked out from the quantity to run and the ups, and
 * follow it unless typed (056). After release a board can still be added; it
 * is issued as it is added (058).
 */
function BoardSection({
  editing, boards, ctx, draft, setDraft, options, canAct, acting,
  boardRequired, live, onUseRun, onSettle, onNeedsBoard, onAddBoard,
}: {
  editing: boolean;
  live: { suggested: number | null; why: string | null } | null;
  boards: JobBoard[]; ctx: BoardContext | null;
  draft: BoardDraft[]; setDraft: (d: BoardDraft[]) => void;
  options: BoardOption[];
  canAct: boolean; acting: boolean; boardRequired: boolean;
  onUseRun: (itemId: number) => void; onSettle: (b: JobBoard) => void;
  onNeedsBoard: () => void; onAddBoard: (itemId: number, sheets: number) => Promise<boolean>;
}) {
  const suggested = live ? live.suggested : ctx?.suggestedSheets ?? null;
  const why = live ? live.why : ctx?.ups && suggested
    ? `${ctx.productionQuantity.toLocaleString('en-IN')} to run ÷ ${ctx.ups} ups`
    : null;
  const released = ctx ? ['CREATED', 'IN_PRODUCTION', 'ON_HOLD'].includes(ctx.status) : false;
  const patch = (i: number, p: Partial<BoardDraft>) =>
    setDraft(draft.map((r, n) => (n === i ? { ...r, ...p } : r)));
  const [adding, setAdding] = useState<BoardDraft | null>(null);
  const pickerOptions = (exclude: number[]) => options
    .filter((o) => !exclude.includes(o.item_id))
    .map((o) => ({
      value: String(o.item_id), label: o.name,
      hint: `${Number(o.free).toLocaleString('en-IN')} free`,
    }));

  const adder = adding && (
    <div className="field board-row" style={{ marginTop: 8 }}>
      <div style={{ flex: 1 }}>
        <SearchSelect id="jc-add-board" value={adding.itemId} autoFocus
                      placeholder="Type to search, e.g. 230 or 61" emptyLabel="Choose the board…"
                      onChange={(v) => setAdding({ ...adding, itemId: v })}
                      options={pickerOptions(boards.map((b) => b.item_id))} />
      </div>
      <input type="number" min="1" step="1" aria-label="Sheets" placeholder="Sheets"
             style={{ width: 110 }} value={adding.sheets}
             onChange={(e) => setAdding({ ...adding, sheets: e.target.value })} />
      <button className="btn primary"
              disabled={acting || !adding.itemId || !(Number(adding.sheets) > 0)}
              onClick={async () => {
                if (await onAddBoard(Number(adding.itemId), Number(adding.sheets))) setAdding(null);
              }}>
        Add and issue
      </button>
      <button className="linkish" onClick={() => setAdding(null)}>Cancel</button>
    </div>
  );
  const addLink = released && canAct && !adding && boards.length < 4 && (
    <button className="linkish"
            onClick={() => setAdding({ itemId: '', sheets: suggested ? String(suggested) : '' })}>
      + Add board
    </button>
  );

  return (
    <>
      <label className="jc-label">Board</label>
      {!editing ? (
        boards.length === 0 ? (
          <div>
            {!boardRequired ? (
              <>
                <span className="dim">Not needed — marked on the order line</span>
                {canAct && (
                  <>{' · '}<button className="linkish" disabled={acting} onClick={onNeedsBoard}>
                    This box needs board
                  </button></>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {released
                  ? <span className="dim">Not recorded — released before board came from stock</span>
                  : <span className="tag waiting">No board chosen yet</span>}
                {addLink}
              </div>
            )}
            {adder}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {boards.map((b) => {
              const gap = Number(b.to_issue);
              return (
                <div key={b.item_id}>
                  <div className="jcs-board-line">
                    <strong>{b.item_name}</strong>
                    <BoardStatus b={b} />
                  </div>
                  <div>
                    {fmtN(b.sheets)} sheets
                    <span className="dim" style={{ fontSize: 12.5 }}>
                      {' · '}{b.set_by_hand || !why ? 'typed' : why}
                    </span>
                  </div>
                  {ctx?.status === 'DRAFT' && Number(b.issued) > 0 && (
                    <div className="hint">
                      {fmtN(b.issued)} already given to this job · release issues the other{' '}
                      {fmtN(Math.max(Number(b.sheets) - Number(b.issued), 0))}
                    </div>
                  )}
                  {Number(b.on_order) > 0 && (
                    <div className="hint">
                      {fmtN(b.on_order)} on order from {b.on_order_from} · due {fmtDate(b.on_order_due)}
                    </div>
                  )}
                  {b.out_of_date && b.run_needs && (
                    <div className="hint" style={{ color: 'var(--soon)' }}>
                      Card says {fmtN(b.sheets)} · run now needs {fmtN(b.run_needs)}
                      {canAct && (
                        <>{' · '}
                          <button className="linkish" disabled={acting} onClick={() => onUseRun(b.item_id)}>
                            Use {fmtN(b.run_needs)}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {gap !== 0 && (
                    <div className="hint" style={{ color: 'var(--soon)' }}>
                      Issued {fmtN(b.issued)} · run needs {fmtN(b.sheets)}
                      {canAct && (
                        <>{' · '}
                          <button className="linkish" disabled={acting} onClick={() => onSettle(b)}>
                            {gap > 0 ? `Issue ${fmtN(gap)} more` : `Return ${fmtN(-gap)}`}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {addLink && <div>{addLink}</div>}
            {adder}
          </div>
        )
      ) : (
        <div className="field">
          {draft.map((r, i) => {
            const n = Number(r.sheets);
            return (
              <div key={i} className="board-row">
                <div style={{ flex: 1 }}>
                  <SearchSelect id={`jc-board-${i}`} value={r.itemId}
                                placeholder="Type to search, e.g. 230 or 61"
                                emptyLabel="Choose the board…"
                                onChange={(v) => patch(i, {
                                  itemId: v,
                                  // First pick fills the sheets in; a typed figure is kept.
                                  ...(v && !r.sheets && suggested ? { sheets: String(suggested) } : {}),
                                })}
                                options={pickerOptions([])} />
                </div>
                <div style={{ width: 110 }}>
                  <input type="number" min="1" step="1" aria-label="Sheets" placeholder="Sheets"
                         value={r.sheets} onChange={(e) => patch(i, { sheets: e.target.value })} />
                  {i === 0 && suggested && n !== suggested && (
                    <div className="hint">
                      Suggested {suggested.toLocaleString('en-IN')}{' '}
                      <button className="linkish" onClick={() => patch(i, { sheets: String(suggested) })}>Use</button>
                    </div>
                  )}
                </div>
                {draft.length > 1 && (
                  <button className="btn" aria-label="Remove this board"
                          onClick={() => setDraft(draft.filter((_, n2) => n2 !== i))}>Remove</button>
                )}
              </div>
            );
          })}
          <div className="hint" style={{ display: 'flex', gap: 12 }}>
            <span>
              {why ? `Sheets = ${why}, rounded up.`
                : 'Enter the ups to have sheets worked out.'}
            </span>
            {draft.length < 4 && (
              <button className="linkish" onClick={() => setDraft([...draft, { itemId: '', sheets: '' }])}>
                + Add another board
              </button>
            )}
          </div>
          {boards.filter((b) => Number(b.on_order) > 0 && !draft.some((r) => r.itemId === String(b.item_id)))
            .map((b) => (
              <div key={b.item_id} className="tag waiting notice" role="alert" style={{ marginTop: 10, fontSize: 13, fontWeight: 500 }}>
                {b.item_name} is on order for this job — {fmtN(b.on_order)} sheets from {b.on_order_from}
                {b.on_order_due ? `, due ${fmtDate(b.on_order_due)}` : ''}. Saving without it sends that
                board to stock when it arrives.
              </div>
            ))}
        </div>
      )}
    </>
  );
}

/**
 * What happened with this job's board outside the normal way (066): sent to
 * production without it, and how that was covered; or board it lent to a job
 * that was. Shown in amber while anything is still owed either way.
 */
function BoardExceptionNotes({ x }: { x: BoardExceptions }) {
  const lent = Number(x.lent_owed ?? 0);
  const borrowed = Number(x.borrowed_owed ?? 0);
  if (x.history.length === 0 && lent === 0) return null;
  const how = (h: BoardExceptions['history'][number]) =>
    h.kind === 'LOAN' ? `took board held for ${h.lender_job_number ?? 'another job'}`
    : h.kind === 'SUBSTITUTE' ? `used ${h.substitute_name ?? 'another board'} instead`
    : `${fmtN(h.sheets_short)} sheets to follow`;
  return (
    <div style={{ marginTop: 8, display: 'grid', gap: 3 }}>
      {lent > 0 && (
        <div className="hint" style={{ color: 'var(--soon)' }}>
          Lent {fmtN(lent)} sheets to {x.lent_to} — this job is short until they are given back
          (Stock › Board › To order).
        </div>
      )}
      {borrowed > 0 && (
        <div className="hint" style={{ color: 'var(--soon)' }}>
          Uses board held for {x.borrowed_from} — {fmtN(borrowed)} sheets still to give back.
        </div>
      )}
      {x.history.map((h, i) => (
        <div key={i} className="hint">
          Sent to production without its board · {fmtDate(String(h.created_at).slice(0, 10))} · {h.by_name}
          {' · '}{how(h)} · “{h.reason}”
        </div>
      ))}
    </div>
  );
}

/**
 * Beside the running size: the board's sheet size to take when it is empty,
 * and a warning when the running size is bigger than the sheet it is cut from.
 */
function RunningSizeHint({ running, board, onUse }: {
  running: string | null; board: [number, number] | null; onUse?: ((v: string) => void) | undefined;
}) {
  if (!board) return null;
  const text = `${board[0]} X ${board[1]} CM`;
  const r = parseSize(running);
  if (r && (r[0] > board[0] || r[1] > board[1])) {
    return <div className="hint" style={{ color: 'var(--late)' }}>Bigger than the board sheet ({text})</div>;
  }
  if (!running?.trim() && onUse) {
    return (
      <div className="hint">
        Board is {text}{' · '}<button className="linkish" onClick={() => onUse(text)}>Use</button>
      </div>
    );
  }
  return null;
}

function Box({
  title, status, editing, busy, onEdit, onCancel, onSave, boxRef, children,
}: {
  title: string; status?: React.ReactNode; editing: boolean; busy: boolean;
  onEdit: (() => void) | null; onCancel: () => void; onSave: () => void;
  boxRef?: React.RefObject<HTMLElement>; children: React.ReactNode;
}) {
  return (
    <section ref={boxRef} className={`jcs-box${editing ? ' editing' : ''}`}>
      <div className="jcs-box-head">
        <h3>{title}</h3>
        {status}
        <div className="spacer" />
        {editing ? (
          <>
            <button className="btn" disabled={busy} onClick={onCancel}>Cancel</button>
            <button className="btn primary" disabled={busy} onClick={onSave}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        ) : onEdit && <button className="linkish" onClick={onEdit}>Edit</button>}
      </div>
      <div className="jcs-box-body">{children}</div>
    </section>
  );
}

function Field({ label, children, req }: { label: string; children: React.ReactNode; req?: boolean }) {
  return (
    <div className="field">
      {label && <label className={req ? 'req' : undefined}>{label}</label>}
      {children}
    </div>
  );
}

function Read({ v }: { v: unknown }) {
  const empty = v === null || v === undefined || v === '';
  return (
    <div className={`jcs-val${empty ? ' dim' : ''}`}>
      {empty ? 'Not set' : String(v)}
    </div>
  );
}
